import { useCallback, useEffect, useRef } from 'react';
import {
  decideQueueImport,
  parseRoute,
  queueCurrent,
  queueHoldsVideo,
  queueVideosToHandOver,
  queueVideosToImport,
  type QueueVideo,
} from '@visious/core';
import { usePlayback } from '../playback/context';
import { queueVideoHref } from '../queue/model';
import {
  clearQueue,
  importIntoQueue,
  readQueue,
  startQueue,
  unlinkQueue,
  useQueue,
} from '../queue/store';
import { currentHref, navigate } from '../router/router';
import { useScreen } from '../router/screens';
import { watchWithoutListHref } from '../screens/watch/model';
import {
  newRequestId,
  postToTabs,
  subscribeToTabs,
  tabsReachable,
  THIS_TAB,
  type TabMessage,
} from './channel';
import {
  importFlow,
  markHandedOver,
  hasHandedOver,
  setImportFlow,
  useImportFlow,
} from './store';

/**
 * Collecting the reader's other Visious tabs into this one.
 *
 * Every tab runs this hook and it plays both parts: it **answers** another tab's collection with the
 * videos it has, and it **runs** one when the header's control is pressed here. One hook, because it
 * is one conversation and because both halves need the same two things — the queue, and what the
 * player is on.
 *
 * ### What the reader gets
 *
 * The videos from the other tabs move into this tab's queue and those tabs close. Nothing navigates: a
 * reader who pressed the control from a channel page stays on the channel page, and the list they
 * just collected plays in the corner. That is why the control is in the header rather than on the
 * watch page — the import is about tabs, not about a video.
 *
 * ### Which tabs are left alone
 *
 * - **A tab watching a playlist**, whether the queue carries the link or the address does. A window
 *   on a playlist is re-projected from the playlist itself, so there is no list of that tab's own to
 *   take, and closing it would end a reader's place in a list they did not mention.
 * - **An embedded player**, which is somebody else's page with a Visious frame in it. It never
 *   mounts this hook at all (`App.tsx` renders /embed outside the shell), and the eligibility test
 *   says so a second time rather than relying on where a component happens to be mounted.
 * - **A tab with nothing to give.** A tab parked on a feed is not one of the tabs the videos came
 *   from, so it makes no offer and is therefore in no `take`.
 *
 * ### What closing a tab can and cannot do
 *
 * `window.close()` only works on a browsing context the browser considers script-closable — one the
 * app opened itself, or one whose session history is a single entry. A tab the reader opened by hand
 * and has been using is neither, so the call is a silent no-op there and **the tab stays open**.
 * That is the platform rather than something to work around, so it has an answer instead: a tab that
 * could not close still gives its videos up, stops playing and drops its list, and says on the watch
 * page that it has to be closed by hand (`tabs/store.ts`). The tab that ran the import hears about
 * it and says so once.
 *
 * ### Why so little of this is React state
 *
 * Taking the videos in changes what `playback/PlaybackHost.tsx` returns, which re-mounts everything
 * under it — including this hook. So the flow lives in a module store, the channel is the module's
 * (`tabs/channel.ts`), and the timers below are deliberately **not** cleared on unmount: each one is
 * a step of an exchange that has to finish, and every one of them writes module state or posts on a
 * module channel rather than touching a component that may be gone.
 */

/**
 * How long the collection listens. Long enough for a tab that is running to answer, short enough to
 * stay a press rather than a wait — and there is no count to wait for (`tabs/channel.ts`), so a
 * clock is the only thing that can end it at all.
 *
 * Nobody waits at a control that has not moved: the press turns it into a spinner for as long as
 * this lasts, and the common case ends well before the clock does, on the first tab to answer.
 */
const COLLECT_MS = 400;

/**
 * How long to listen for tabs saying they could not close. They answer about
 * {@link CLOSE_REPORT_MS} after the take, so this is that plus room for a busy main thread.
 */
const REPORT_MS = 600;

/**
 * How long a tab waits after `close()` before deciding it is still here. A tab that really is going
 * fires `pagehide` first, and that is what tells the two apart: a timer alone would report a slow
 * close as a refusal.
 */
const CLOSE_REPORT_MS = 150;

/**
 * How long the "no other tabs" line stays under the control. It answers a press and nothing else is
 * waiting on it, so it takes itself back rather than asking to be dismissed.
 */
const NOTICE_MS = 4000;

export interface TabImportBinding {
  /** The confirmation is up, in whichever of its three faces. */
  readonly open: boolean;
  /**
   * This tab's own queue is a window on a playlist, so the confirmation says so and offers the
   * second answer. True for a `list` in the address as well as for a link in the queue: the panel's
   * loader turns the first into the second on the next render.
   */
  readonly linked: boolean;
  /** The collection is out, or the tabs are being told to close. */
  readonly busy: boolean;
  /** At least one tab handed its videos over and the browser would not let it close. */
  readonly stayed: boolean;
  /** Import, and let go of a playlist this tab was watching through. */
  readonly importNow: () => void;
  /** Import, and keep that playlist's videos as a list of this tab's own first. */
  readonly unlinkAndImport: () => void;
  readonly close: () => void;
}

/** A collection in flight: which request it is, and what has answered so far. */
interface Collection {
  readonly request: string;
  readonly offers: Map<string, readonly QueueVideo[]>;
  /**
   * Called when the **first** tab answers, and `null` for a collection nothing is waiting on. It is
   * how the press below opens the confirmation without waiting out its own clock.
   */
  readonly onFirst: (() => void) | null;
}

/** The window after the take, in which a tab that could not close says so. */
interface Report {
  readonly request: string;
  stayed: boolean;
}

/**
 * The exchange in flight, module state for the flow's own reason: the act of taking the videos in
 * re-mounts this hook, and the tabs that could not close answer *after* that. Held in a ref instead,
 * their answers would arrive at an instance that no longer exists and the reader would be told
 * everything closed.
 *
 * One of each, which is all there can be: the flow state allows exactly one import at a time.
 */
let collection: Collection | null = null;
let report: Report | null = null;

/**
 * Give the videos up and go.
 *
 * A module function rather than a callback, because the tab it runs in is re-mounting while it runs:
 * clearing the list changes what the playback host returns. The timer therefore belongs to the
 * module too, or the report this tab owes the other one would be cancelled by the very re-mount its
 * first line caused.
 *
 * The list is cleared and the handover recorded **before** `close()`, so that a tab the browser
 * refuses is already in the right state by the time it finds out: no playback, no queue, and the
 * watch page saying what happened. Nothing flushes a resume position on the way — the videos are
 * being watched in another tab now, and writing where this one had got to would put a resume marker
 * in front of the reader for a video they are still watching.
 */
function handOver(request: string): void {
  clearQueue();
  markHandedOver();

  let leaving = false;
  const onHide = (): void => {
    leaving = true;
  };
  globalThis.addEventListener('pagehide', onHide, { once: true });
  globalThis.close();

  globalThis.setTimeout(() => {
    globalThis.removeEventListener('pagehide', onHide);
    if (leaving) return;
    postToTabs({ kind: 'stayed', request, tab: THIS_TAB });
  }, CLOSE_REPORT_MS);
}

/**
 * How many "no other tabs" lines have been shown. A generation counter rather than a timer handle,
 * because the line's own clock has to know whether it is still clearing the line it put up: a reader
 * who presses again while it is on screen gets a second one, and the first one's timer must not take
 * that one away.
 */
let notices = 0;

/** Say there is nothing to move, and take it back on a clock. */
function sayNothingToMove(): void {
  const shown = ++notices;
  setImportFlow('empty');
  globalThis.setTimeout(() => {
    if (notices !== shown || importFlow() !== 'empty') return;
    setImportFlow('idle');
  }, NOTICE_MS);
}

/**
 * The header's control: ask whether any other tab has videos, and open the confirmation only if one
 * answers. Nothing to move gets a line under the control instead — a dialog whose only answer is
 * "all right" is a press the reader has to make for nothing.
 *
 * A page cannot enumerate tabs, so this question can only be asked of the tabs themselves and only
 * when somebody wants the answer. What the reader waits at is therefore the control: it holds a
 * spinner and takes no presses until one of the two answers arrives.
 *
 * A module function rather than a callback, for `tabs/store.ts`'s reason: the control is in the
 * header and the dialog is inside `PlaybackHost`, and no component is above both.
 *
 * **What answers here is not what is taken.** The import collects again once the reader has said
 * yes, because a list gathered before a dialog somebody can sit in front of is a list that has had
 * time to go stale — a tab may have moved on, or filled up, or gone. The cost is a second round of
 * the same messages, which is one broadcast and a clock.
 */
export function askToImportTabs(): void {
  // A press that arrived some other way than the control (a keyboard repeat, a script) while one is
  // already out. The control itself is disabled for exactly this window.
  if (importFlow() === 'probing') return;
  if (!tabsReachable()) {
    sayNothingToMove();
    return;
  }
  setImportFlow('probing');

  const request = newRequestId();
  /*
   * The confirmation goes up on the *first* answer rather than at the end of the window. One tab is
   * all it takes for there to be something to ask about, and the clock below is only there for the
   * case where nobody is listening at all — waiting it out would put 400 ms in front of every press.
   */
  const asked: Collection = {
    request,
    offers: new Map(),
    onFirst: () => setImportFlow('asking'),
  };
  collection = asked;
  postToTabs({ kind: 'collect', request });

  globalThis.setTimeout(() => {
    if (collection !== asked) return;
    collection = null;
    if (asked.offers.size === 0 && importFlow() === 'probing') sayNothingToMove();
  }, COLLECT_MS);
}

export function useTabImport(): TabImportBinding {
  const flow = useImportFlow();
  const queue = useQueue();
  const screen = useScreen();
  const playback = usePlayback();

  const routePlaylistId = screen.kind === 'watch' ? screen.playlistId : null;
  const linked = queue.link !== null || routePlaylistId !== null;

  /**
   * What is playing, as a row a queue could hold.
   *
   * The binding's record is the first answer and the queue's own current row is the second, which is
   * what covers a video whose payload is still on the wire or which the instance refused: a linked
   * queue always has a row for the address's video, since its cursor is placed on it, and that row
   * is what keeps the video in the list when the link is let go.
   */
  const leadVideo = useCallback((): QueueVideo | null => {
    const video = playback?.binding.video ?? null;
    if (video !== null) {
      return {
        // The binding's id rather than the record's: it is known from the moment a session exists.
        videoId: playback?.binding.videoId ?? video.videoId,
        title: video.title,
        author: video.author,
        authorHref: video.authorHref,
        thumbnailUrl: video.thumbnailUrl,
        durationSec: video.durationSec,
      };
    }
    const current = queueCurrent(readQueue());
    if (current === null) return null;
    return {
      videoId: current.videoId,
      title: current.title,
      author: current.author,
      authorHref: current.authorHref,
      thumbnailUrl: current.thumbnailUrl,
      durationSec: current.durationSec,
    };
  }, [playback]);

  /* ----------------------------------------------------------------------------------------------
   * Answering somebody else's collection
   * -------------------------------------------------------------------------------------------- */

  const eligible =
    screen.kind !== 'embed' && queue.link === null && routePlaylistId === null && !hasHandedOver();

  const respond = useCallback(
    (request: string): void => {
      if (!eligible) return;
      const videos = queueVideosToHandOver(readQueue(), leadVideo());
      if (videos.length === 0) return;
      postToTabs({ kind: 'offer', request, tab: THIS_TAB, videos });
    },
    [eligible, leadVideo],
  );

  /* ----------------------------------------------------------------------------------------------
   * Running one
   * -------------------------------------------------------------------------------------------- */

  /**
   * The collected videos into this tab's list, in whichever of the four shapes the list is in
   * (`core/queue/handover.ts` decides, and says why each one is what it is).
   *
   * The address is settled last and only on a `list` route, because both of the acts that end a
   * playlist window have to end it in the address too: leave `list` behind and the panel's loader
   * reads the playlist straight back into the queue on the next render, taking the import with it.
   */
  const apply = useCallback(
    (videos: readonly QueueVideo[], unlink: boolean): void => {
      const state = readQueue();
      const lead = leadVideo();
      const action = decideQueueImport({
        queue: state,
        playingVideoId: lead?.videoId ?? null,
        unlink,
      });
      const keeps = action === 'append' || action === 'unlinkAndAppend';
      const held = keeps
        ? new Set(state.entries.map((entry) => entry.videoId))
        : new Set(lead === null ? [] : [lead.videoId]);
      const take = queueVideosToImport(videos, held);
      // Every offered video is already in this list. The tabs still close, since they handed over
      // what they had, but there is nothing here to change and least of all a link to let go of.
      if (take.length === 0) return;

      if (action === 'unlinkAndAppend') unlinkQueue();
      if (keeps) importIntoQueue(take);
      else if (lead === null) startQueue(take, true);
      else startQueue([lead, ...take], false);

      /*
       * The address as it stands rather than as it stood when the press happened: this runs a
       * moment after the collection went out, and a reader who navigated in the meantime must not
       * be pulled back to the page they left.
       */
      const route = parseRoute(currentHref());
      if (route.kind !== 'watch' || route.playlistId === null) return;
      /*
       * Arriving at a `/watch?v=` the queue does not hold discards the queue
       * (`queue/useQueueAdvance.ts`), and dropping `list` from the address is exactly such an
       * arrival — so the address may only lose the playlist while the list holds the video it names.
       * It does in every arm that had something playing, which is every arm reachable from a
       * playlist route bar one: a page whose video has not loaded and whose queue is still empty has
       * nothing of itself to keep, and there the address follows the new list instead.
       */
      if (queueHoldsVideo(readQueue(), route.videoId)) {
        navigate(watchWithoutListHref(route.videoId, route.startSec), { replace: true });
        return;
      }
      const first = take[0];
      if (first !== undefined) navigate(queueVideoHref(first.videoId));
    },
    [leadVideo],
  );

  /**
   * The two answers the confirmation offers, which differ in one thing: whether a playlist this tab
   * was watching through is kept as a list of its own first.
   *
   * Three steps on two clocks, because there is no count of tabs to wait for. Ask; a moment later
   * take what answered and tell those tabs to go; a moment after that, say whether any of them could
   * not. A tab that answers after the collection has closed is in no `take` and keeps its list.
   */
  const run = useCallback(
    (unlink: boolean): void => {
      if (!tabsReachable()) {
        setImportFlow('idle');
        return;
      }
      setImportFlow('working');

      const request = newRequestId();
      const asked: Collection = { request, offers: new Map(), onFirst: null };
      collection = asked;
      postToTabs({ kind: 'collect', request });

      globalThis.setTimeout(() => {
        collection = null;
        if (asked.offers.size === 0) {
          // The tabs that answered the press have gone in the meantime. Rare, and the reader still
          // gets the answer their press earned rather than a dialog that closes over nothing.
          sayNothingToMove();
          return;
        }
        apply([...asked.offers.values()].flat(), unlink);

        const waiting: Report = { request, stayed: false };
        report = waiting;
        postToTabs({ kind: 'take', request, tabs: [...asked.offers.keys()] });

        globalThis.setTimeout(() => {
          report = null;
          // Silence is the good outcome and gets no sentence: the only thing worth saying is that
          // some tabs are still open.
          setImportFlow(waiting.stayed ? 'stayed' : 'idle');
        }, REPORT_MS);
      }, COLLECT_MS);
    },
    [apply],
  );

  /* ----------------------------------------------------------------------------------------------
   * The channel
   * -------------------------------------------------------------------------------------------- */

  /**
   * The handler, mirrored into a ref so that the subscription is made once per mount while still
   * answering out of the current render's state. Re-subscribing per render would be harmless on this
   * channel but would put a set-membership change on every one of the four-times-a-second renders
   * the transport poll causes.
   */
  const onMessage = (message: TabMessage): void => {
    switch (message.kind) {
      case 'collect':
        respond(message.request);
        return;
      case 'offer': {
        if (collection === null || collection.request !== message.request) return;
        const first = collection.offers.size === 0;
        collection.offers.set(message.tab, message.videos);
        if (first) collection.onFirst?.();
        return;
      }
      case 'take':
        if (message.tabs.includes(THIS_TAB)) handOver(message.request);
        return;
      case 'stayed':
        if (report === null || report.request !== message.request) return;
        report.stayed = true;
        return;
    }
  };
  const handlerRef = useRef(onMessage);
  useEffect(() => {
    handlerRef.current = onMessage;
  });

  useEffect(() => subscribeToTabs((message) => handlerRef.current(message)), []);

  return {
    // `probing` and `empty` are the two the header answers for: the first is the control's own
    // spinner, and the second is a line under it rather than a dialog.
    open: flow === 'asking' || flow === 'working' || flow === 'stayed',
    linked,
    busy: flow === 'working',
    stayed: flow === 'stayed',
    importNow: useCallback(() => run(false), [run]),
    unlinkAndImport: useCallback(() => run(true), [run]),
    close: useCallback(() => setImportFlow('idle'), []),
  };
}
